import { ArrowRight, Archive, Ban, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { DeliveryState, TotadminPermission } from 'data/__generated__';

import { DELIVERY_STATES_LABELS, getColorsFromState } from '../constants/states';
import { DeliveryStateAction } from './useDeliveryStateActions';

/**
 * Le statut de la livraison et, à côté, les seules transitions possibles depuis là.
 *
 * L'ancienne fiche empilait les six transitions en permanence, cinq d'entre elles grisées sur une
 * livraison qui vient d'être créée : une colonne entière pour une seule action faisable.
 */
export const DeliveryStateCard = ({
    state,
    actions,
    loading,
}: {
    state: DeliveryState;
    actions: DeliveryStateAction[];
    loading: boolean;
}) => {
    const { backgroundColor, labelColor } = getColorsFromState(state);
    const closingStates = [DeliveryState.Aborted, DeliveryState.Archived];
    const nextActions = actions.filter(({ state: targetState }) => !closingStates.includes(targetState));
    const abortAction = actions.find(({ state: targetState }) => targetState === DeliveryState.Aborted);
    const archiveAction = actions.find(({ state: targetState }) => targetState === DeliveryState.Archived);

    return (
        <div
            className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-lg border border-border bg-card px-4 py-3"
            data-test="delivery-state-card"
        >
            <div className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Statut</span>
                <Badge
                    className="rounded-full border-0 px-4 py-1 text-sm font-medium"
                    style={{ backgroundColor: `${backgroundColor}dd`, color: labelColor }}
                    data-test="delivery-state"
                >
                    {DELIVERY_STATES_LABELS[state]}
                </Badge>
            </div>

            {actions.length ? <ArrowRight className="size-4 shrink-0 text-muted-foreground" aria-hidden /> : null}

            <div className="flex flex-wrap items-center gap-2">
                {nextActions.map(({ state: targetState, label, run }) => (
                    <Button
                        key={targetState}
                        type="button"
                        variant="outline"
                        disabled={loading}
                        requiredPermissions={[TotadminPermission.DeliveriesManage]}
                        onClick={() => void run()}
                        data-test={`delivery-state-action-${targetState}`}
                    >
                        {loading ? <Loader2 className="animate-spin" /> : null}
                        {label}
                    </Button>
                ))}
                {archiveAction ? (
                    <Button
                        type="button"
                        variant="outline"
                        disabled={loading}
                        requiredPermissions={[TotadminPermission.DeliveriesManage]}
                        onClick={() => void archiveAction.run()}
                        data-test={`delivery-state-action-${DeliveryState.Archived}`}
                    >
                        <Archive />
                        {archiveAction.label}
                    </Button>
                ) : null}
                {abortAction ? (
                    <Button
                        type="button"
                        variant="outline"
                        className="text-destructive hover:text-destructive"
                        disabled={loading}
                        requiredPermissions={[TotadminPermission.DeliveriesManage]}
                        onClick={() => void abortAction.run()}
                        data-test={`delivery-state-action-${DeliveryState.Aborted}`}
                    >
                        <Ban />
                        {abortAction.label}
                    </Button>
                ) : null}
                {!actions.length ? (
                    <span className="text-sm text-muted-foreground">Aucune action possible depuis ce statut.</span>
                ) : null}
            </div>
        </div>
    );
};
